import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import MultiSelectCst from "@components/InputCst/MultiSelectCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import { WizardStepProps } from "@components/WizardCst/types";
import { UserWizardData } from "../types";
import {
  FieldGrid,
  SectionDivider,
  SectionHeader,
  StepContainer,
} from "../components/StepLayout";
import QuestionToggle from "../components/QuestionToggle";
import UogSection from "../components/UogSection";
import MestieriSection from "../components/MestieriSection";
import { useUserCustomFields } from "../customFieldsContext";

const AdditionalInfoStep: React.FC<WizardStepProps<UserWizardData>> = ({
  data,
  setData,
}) => {
  const { t } = useTranslation("user-wizard-v2");
  const { id: corporateId = "" } = useParams();
  const customFieldsCatalog = useUserCustomFields();

  const text = (key: keyof UserWizardData, label: string) => (
    <InputCst
      id={`user-${String(key)}`}
      label={label}
      value={(data[key] as string) ?? ""}
      onChange={(e: any) => setData({ [key]: e.target.value } as any)}
    />
  );

  const customKeys = Object.keys(customFieldsCatalog);

  return (
    <StepContainer>
      <FieldGrid>
        {text("phone_number", t("fields.phone"))}
        {text("address", t("fields.address"))}
        {text("gender", t("fields.gender"))}
        {text("fiscal_code", t("fields.fiscalCode"))}
        {text("birth_country", t("fields.birthPlace"))}
        {text("birth_province", t("fields.birthProvince"))}
      </FieldGrid>

      <DataPickerCst
        id="user-birthday"
        label={t("fields.birthDate")}
        value={data.birthday}
        onChange={(value: any) => setData({ birthday: value })}
      />

      <SectionDivider />

      <SectionHeader
        title={t("sections.companyInfo")}
        description={t("sections.companyInfoDesc")}
      />
      <FieldGrid>
        <Box
          sx={{
            ml: "1rem",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "text.primary",
              mb: "0.5rem",
            }}
          >
            {t("questions.newHireLabel")}
          </Typography>
          <QuestionToggle
            question={t("questions.newHire")}
            checked={data.new_hire_flag}
            onChange={(next) => setData({ new_hire_flag: next })}
            noLabel={t("common.no")}
            yesLabel={t("common.yes")}
          />
        </Box>
        <Box
          sx={{
            ml: "1rem",
            flexWrap: "nowrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "text.primary",
              mb: 1,
            }}
          >
            {t("questions.smartWorkingLabel")}
          </Typography>
          <QuestionToggle
            question={t("questions.smartWorking")}
            checked={data.smart_working_flag}
            onChange={(next) => setData({ smart_working_flag: next })}
            noLabel={t("common.no")}
            yesLabel={t("common.yes")}
          />
        </Box>
      </FieldGrid>

      <FieldGrid>
        {text("branch", t("fields.branch"))}
        {text("ateco_code", t("fields.atecoCode"))}
        {text("country_code", t("fields.countryCode"))}
        {text("country_description", t("fields.countryDescription"))}
        {text("supervisor", t("fields.supervisor"))}
        {text("contractual_framework_code", t("fields.frameworkCode"))}
        {text("contractual_framework_type", t("fields.frameworkType"))}
        <SelectCst
          id="user-contract-type"
          label={t("fields.contractType")}
          value={data.contract_type}
          optionsKey="typeContract"
          onChange={(value: any) => setData({ contract_type: value })}
        />
        {text("ccnl", t("fields.ccnl"))}
        <MultiSelectCst
          id="user-persona"
          label={t("fields.persona")}
          value={data.persona}
          options={[]}
          optionsKey="personas"
          onChange={(value: any) => setData({ persona: value })}
        />
      </FieldGrid>

      <FieldGrid>
        <DataPickerCst
          id="user-hire-date"
          label={t("fields.hireDate")}
          value={data.hire_date}
          onChange={(value: any) => setData({ hire_date: value })}
        />
        <DataPickerCst
          id="user-termination-date"
          label={t("fields.terminationDate")}
          value={data.termination_date}
          onChange={(value: any) => setData({ termination_date: value })}
        />
        {text("schedule_type", t("fields.scheduleType"))}
        {text("smart_learning_hours", t("fields.smartLearningHours"))}
      </FieldGrid>

      {customKeys.length > 0 && (
        <>
          <SectionDivider />
          <SectionHeader title={t("sections.otherInfo")} />
          <FieldGrid>
            {customKeys.map((key) => (
              <InputCst
                key={key}
                id={`user-${key}`}
                label={customFieldsCatalog[key]}
                value={data.customFields?.[key] ?? ""}
                onChange={(e: any) =>
                  setData((prev) => ({
                    customFields: {
                      ...prev.customFields,
                      [key]: e.target.value,
                    },
                  }))
                }
              />
            ))}
          </FieldGrid>
        </>
      )}

      <SectionDivider />
      <UogSection
        value={data.uog}
        onChange={(next) => setData({ uog: next })}
      />

      <SectionDivider />
      <MestieriSection
        corporateId={corporateId}
        title={t("mestieri.title")}
        labels={{
          industrial: t("mestieri.industrial"),
          professional: t("mestieri.professional"),
          job: t("mestieri.job"),
        }}
        value={{
          industry: data.industry,
          profession: data.profession,
          job: data.job,
        }}
        onChange={(patch) => setData(patch)}
      />

      <SectionDivider />
      <MestieriSection
        corporateId={corporateId}
        title={t("mestieri.titleToBe")}
        labels={{
          industrial: t("mestieri.industrialToBe"),
          professional: t("mestieri.professionalToBe"),
          job: t("mestieri.jobToBe"),
        }}
        value={{
          industry: data.industry_to_be,
          profession: data.profession_to_be,
          job: data.job_to_be,
        }}
        onChange={(patch) => {
          const mapped: Partial<UserWizardData> = {};
          if ("industry" in patch) mapped.industry_to_be = patch.industry;
          if ("profession" in patch) mapped.profession_to_be = patch.profession;
          if ("job" in patch) mapped.job_to_be = patch.job;
          setData(mapped);
        }}
      />
    </StepContainer>
  );
};

export default AdditionalInfoStep;
